import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  RemoveCircleOutline as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface Scenario {
  /** slug = doc id em evo_fixtures/scenarios/items */
  id: string;
  name: string;
  /** Payload completo da venda EVO usado como template para os overrides. */
  basePayload: Record<string, any>;
  /** Payload completo do membro EVO usado como template para members/items. */
  baseMember?: Record<string, any>;
  /** Payload completo do responsável financeiro quando o cenário usa responsibles. */
  baseResponsibleMember?: Record<string, any>;
}

interface ComboLite {
  id: string;
  codigo: string;
  nome: string;
  periodo: number;
  isActive: boolean;
}

interface SaleItemOverride {
  description: string;
  itemValue: number;
  saleValue: number;
}

interface ResponsibleOverride {
  idResponsible: string;
  idMember: string;
  name: string;
}

interface Overrides {
  idSale: string;
  document: string;
  saleItens: SaleItemOverride[];
  receivingDate: string; // ISO date string ou vazio para null
  responsibleEnabled: boolean;
  responsible: ResponsibleOverride;
}

interface DevModePanelProps {
  open: boolean;
  onClose: () => void;
  companyDns: string;
  companyToken: string;
  companyId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
const WEBHOOK_HANDLER_BASE = `https://us-central1-${projectId}.cloudfunctions.net/evoWebhookHandler`;

/**
 * Lê todos os docs em evo_fixtures/sales/items e retorna o próximo idSale
 * sequencial não usado (max numérico + 1). Começa de 10001 se vazio.
 */
async function nextSequentialIdSale(): Promise<string> {
  const snap = await getDocs(collection(db, 'evo_fixtures', 'sales', 'items'));
  let max = 10000;
  snap.docs.forEach((d) => {
    const n = Number(d.id);
    if (!isNaN(n) && n > max) max = n;
  });
  return String(max + 1);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function splitName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

function getSaleResponsible(payload: any): any | null {
  if (Array.isArray(payload?.responsibles) && payload.responsibles.length > 0) {
    return payload.responsibles[0];
  }
  return null;
}

function buildMemberFixture(
  baseMember: Record<string, any> | undefined,
  payloadMember: Record<string, any> | undefined,
): Record<string, any> | null {
  const source = payloadMember ?? baseMember;
  const idMember = source?.idMember ?? baseMember?.idMember;
  if (idMember == null) return null;
  const payloadContacts = payloadMember?.contacts;

  return {
    ...(baseMember ?? {}),
    ...(payloadMember ?? {}),
    idMember,
    contacts: Array.isArray(payloadContacts)
      ? payloadContacts
      : baseMember?.contacts,
  };
}

function buildResponsibleMemberFixture(
  baseMember: Record<string, any> | undefined,
  responsible: any,
): Record<string, any> | null {
  const idMember = responsible?.idMember ?? baseMember?.idMember;
  if (idMember == null && responsible?.idResponsible == null) return null;

  const { firstName, lastName } = splitName(String(responsible?.name ?? ''));
  const baseContacts = baseMember?.contacts;
  return {
    ...(baseMember ?? {}),
    idMember: idMember ?? responsible.idResponsible,
    firstName: firstName || baseMember?.firstName || '',
    lastName: lastName || baseMember?.lastName || '',
    document: responsible?.document ?? responsible?.cpf ?? baseMember?.document,
    email: responsible?.email ?? baseMember?.email,
    contacts: Array.isArray(baseContacts) ? baseContacts : [],
  };
}

/**
 * Aplica overrides ao payload base, preservando demais campos.
 */
function applyOverrides(base: any, ovr: Overrides): any {
  const result = JSON.parse(JSON.stringify(base ?? {}));

  // idSale
  if (ovr.idSale) {
    result.idSale = isNaN(Number(ovr.idSale)) ? ovr.idSale : Number(ovr.idSale);
  }

  // member.document
  if (!result.member) result.member = {};
  if (ovr.document) {
    result.member.document = ovr.document;
  }

  // saleItens (substituição completa se houver overrides definidos)
  if (ovr.saleItens.length > 0) {
    result.saleItens = ovr.saleItens.map((item, idx) => ({
      idSaleItem: idx + 1,
      description: item.description,
      itemValue: item.itemValue,
      saleValue: item.saleValue,
      item: item.description.split(' ')[0]?.toUpperCase() ?? 'ITEM',
    }));
  }

  // receivables[0].receivingDate
  if (Array.isArray(result.receivables) && result.receivables.length > 0) {
    result.receivables[0].receivingDate = ovr.receivingDate
      ? new Date(ovr.receivingDate).toISOString()
      : null;
  }

  // responsibles (adiciona/remove)
  delete result.responsible;
  if (ovr.responsibleEnabled) {
    const responsible = {
      idResponsible: Number(ovr.responsible.idResponsible) || 99001,
      idMember: Number(ovr.responsible.idMember) || 20003,
      name: ovr.responsible.name || 'CARLOS RESPONSÁVEL PAGADOR',
      financialResponsible: true,
    };
    result.responsibles = [responsible];
  } else {
    delete result.responsibles;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

const DevModePanel: React.FC<DevModePanelProps> = ({
  open,
  onClose,
  companyDns,
  companyToken,
  companyId,
}) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [combos, setCombos] = useState<ComboLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [basePayload, setBasePayload] = useState<any>(null);
  const [overrides, setOverrides] = useState<Overrides>({
    idSale: '',
    document: '',
    saleItens: [],
    receivingDate: todayISO(),
    responsibleEnabled: false,
    responsible: {
      idResponsible: '99001',
      idMember: '20003',
      name: 'CARLOS RESPONSÁVEL PAGADOR',
    },
  });
  const [firing, setFiring] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // ---- Load scenarios + combos quando abre ----
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const [scenSnap, combosSnap] = await Promise.all([
          getDocs(collection(db, 'evo_fixtures', 'scenarios', 'items')),
          getDocs(collection(db, 'combos')),
        ]);

        if (cancelled) return;

        const items: Scenario[] = scenSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: (data.name as string) ?? d.id,
            basePayload: (data.basePayload as Record<string, any>) ?? {},
            baseMember: (data.baseMember as Record<string, any>) ?? undefined,
            baseResponsibleMember:
              (data.baseResponsibleMember as Record<string, any>) ?? undefined,
          };
        });
        items.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        setScenarios(items);

        const comboList: ComboLite[] = combosSnap.docs
          .map((d) => ({
            id: d.id,
            codigo: (d.data().codigo as string) ?? '',
            nome: (d.data().nome as string) ?? '',
            periodo: (d.data().periodo as number) ?? 1,
            isActive: (d.data().isActive as boolean) ?? false,
          }))
          .filter((c) => c.isActive)
          .sort((a, b) => b.codigo.length - a.codigo.length);
        setCombos(comboList);
      } catch (err) {
        setSnackbar({
          open: true,
          message: err instanceof Error ? err.message : 'Erro ao carregar dados',
          severity: 'error',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // ---- Quando cenário muda, carrega payload base + popula overrides ----
  useEffect(() => {
    if (!selectedScenarioId) {
      setBasePayload(null);
      return;
    }
    const scenario = scenarios.find((s) => s.id === selectedScenarioId);
    if (!scenario) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const nextId = await nextSequentialIdSale();
        if (cancelled) return;

        const data = scenario.basePayload;
        setBasePayload(data);
        const responsible = getSaleResponsible(data);

        const items: SaleItemOverride[] = Array.isArray(data.saleItens)
          ? data.saleItens.map((it: any) => ({
              description: it.description ?? '',
              itemValue: Number(it.itemValue ?? 0),
              saleValue: Number(it.saleValue ?? 0),
            }))
          : [];

        const firstReceivable = Array.isArray(data.receivables)
          ? data.receivables[0]
          : null;
        const recvDate = firstReceivable?.receivingDate
          ? new Date(firstReceivable.receivingDate).toISOString().slice(0, 10)
          : '';

        setOverrides({
          idSale: nextId, // Sempre novo por padrão. Edita pra reusar e testar idempotência.
          document:
            scenario.baseMember?.document ??
            data.member?.document ??
            '',
          saleItens: items,
          receivingDate: recvDate,
          responsibleEnabled: !!responsible,
          responsible: {
            idResponsible: String(responsible?.idResponsible ?? 99001),
            idMember: String(
              responsible?.idMember ??
                scenario.baseResponsibleMember?.idMember ??
                20003,
            ),
            name:
              responsible?.name ??
              `${scenario.baseResponsibleMember?.firstName ?? 'CARLOS RESPONSÁVEL'} ${scenario.baseResponsibleMember?.lastName ?? 'PAGADOR'}`.trim(),
          },
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err instanceof Error ? err.message : 'Erro ao carregar cenário',
          severity: 'error',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedScenarioId, scenarios]);

  // ---- Payload final (memoizado) ----
  const finalPayload = useMemo(() => {
    if (!basePayload) return null;
    return applyOverrides(basePayload, overrides);
  }, [basePayload, overrides]);

  // ---- Handlers ----
  const handleFire = async () => {
    if (!finalPayload) return;
    if (!companyId) {
      setSnackbar({
        open: true,
        message: 'Empresa sem companyId vinculado. Vincule a empresa antes de testar.',
        severity: 'error',
      });
      return;
    }
    try {
      setFiring(true);

      // 1. Persiste payload customizado em evo_fixtures/sales/items/{idSale}.
      //    O backend (mock mode) vai ler este doc via getMockSaleById(IdRecord).
      const idSale = String(finalPayload.idSale);
      const scenario = scenarios.find((s) => s.id === selectedScenarioId);
      const memberFixture = buildMemberFixture(
        scenario?.baseMember,
        finalPayload.member,
      );
      const responsible = getSaleResponsible(finalPayload);
      const responsibleFixture = overrides.responsibleEnabled
        ? buildResponsibleMemberFixture(
            scenario?.baseResponsibleMember,
            responsible,
          )
        : null;

      await setDoc(
        doc(db, 'evo_fixtures', 'sales', 'items', idSale),
        finalPayload,
        { merge: true },
      );

      if (memberFixture?.idMember != null) {
        await setDoc(
          doc(
            db,
            'evo_fixtures',
            'members',
            'items',
            String(memberFixture.idMember),
          ),
          memberFixture,
          { merge: true },
        );
      }

      if (responsible && responsibleFixture) {
        const ids = [
          responsible.idResponsible,
          responsible.idMember,
        ].filter((id, idx, arr) => id != null && arr.indexOf(id) === idx);

        await Promise.all(
          ids.map((id) =>
            setDoc(
              doc(db, 'evo_fixtures', 'members', 'items', String(id)),
              responsibleFixture,
              { merge: true },
            ),
          ),
        );
      }

      // 2. Dispara webhook com formato EvoWebhookPayload real
      //    (?id=companyId na query, body com EventType + IdRecord).
      const url = `${WEBHOOK_HANDLER_BASE}?id=${encodeURIComponent(companyId)}`;
      const webhookBody = {
        ApiCallback: '',
        EventType: 'NewSale',
        IdBranch: 0,
        IdRecord: idSale,
        IdW12: 0,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(webhookBody),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text.slice(0, 200)}`);
      }
      setSnackbar({
        open: true,
        message: `Webhook NewSale disparado com sucesso (IdRecord=${idSale}).`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Erro ao disparar',
        severity: 'error',
      });
    } finally {
      setFiring(false);
    }
  };

  const updateOverride = <K extends keyof Overrides>(key: K, value: Overrides[K]) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const updateSaleItem = (
    idx: number,
    patch: Partial<SaleItemOverride>,
  ) => {
    setOverrides((prev) => {
      const items = [...prev.saleItens];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, saleItens: items };
    });
  };

  const addSaleItem = () => {
    setOverrides((prev) => ({
      ...prev,
      saleItens: [
        ...prev.saleItens,
        { description: '', itemValue: 0, saleValue: 0 },
      ],
    }));
  };

  const removeSaleItem = (idx: number) => {
    setOverrides((prev) => ({
      ...prev,
      saleItens: prev.saleItens.filter((_, i) => i !== idx),
    }));
  };

  // ---- Render ----
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Dev Mode — Testar Cenário EVO
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Empresa: <strong>{companyDns || '—'}</strong> · companyId:{' '}
            <code>{companyId?.slice(0, 12) || '—'}…</code>
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loading && !basePayload ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* ---- Combos ativos em staging ---- */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Combos ativos em staging ({combos.length})
                </Typography>
                {combos.length === 0 ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Nenhum combo ativo. Cadastre via <code>/combos</code> antes
                    de simular vendas que façam match.
                  </Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell align="right">Período</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {combos.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Chip
                              label={c.codigo}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>{c.nome}</TableCell>
                          <TableCell align="right">{c.periodo}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <Typography variant="caption" color="text.secondary">
                  Para fazer match, a <code>description</code> de algum item da
                  venda deve conter o código (word-boundary). Códigos mais
                  longos têm prioridade.
                </Typography>
              </Box>

              <Divider />

              {/* ---- Cenário base ---- */}
              <FormControl fullWidth size="small">
                <InputLabel>Cenário base</InputLabel>
                <Select
                  value={selectedScenarioId}
                  label="Cenário base"
                  onChange={(e) => setSelectedScenarioId(e.target.value)}
                >
                  {scenarios.length === 0 && (
                    <MenuItem disabled value="">
                      Nenhum cenário encontrado em evo_fixtures/scenarios/items
                    </MenuItem>
                  )}
                  {scenarios.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ---- Overrides ---- */}
              {basePayload && (
                <>
                  <Divider>Overrides</Divider>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      label="idSale (sequencial — edite pra reusar e testar idempotência)"
                      size="small"
                      value={overrides.idSale}
                      onChange={(e) => updateOverride('idSale', e.target.value)}
                      fullWidth
                    />
                    <IconButton
                      size="small"
                      title="Próximo idSale sequencial não usado"
                      onClick={async () => {
                        const id = await nextSequentialIdSale();
                        updateOverride('idSale', id);
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Stack>

                  <TextField
                    label="CPF do membro (member.document)"
                    size="small"
                    value={overrides.document}
                    onChange={(e) => updateOverride('document', e.target.value)}
                    fullWidth
                  />

                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="subtitle2">
                        Items da venda ({overrides.saleItens.length})
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={addSaleItem}
                      >
                        Adicionar item
                      </Button>
                    </Stack>
                    <Stack spacing={1}>
                      {overrides.saleItens.map((item, idx) => (
                        <Stack
                          key={idx}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <TextField
                            label="description (match combo)"
                            size="small"
                            value={item.description}
                            onChange={(e) =>
                              updateSaleItem(idx, { description: e.target.value })
                            }
                            sx={{ flex: 2 }}
                          />
                          <TextField
                            label="itemValue"
                            size="small"
                            type="number"
                            value={item.itemValue}
                            onChange={(e) =>
                              updateSaleItem(idx, {
                                itemValue: Number(e.target.value),
                              })
                            }
                            sx={{ width: 110 }}
                          />
                          <TextField
                            label="saleValue"
                            size="small"
                            type="number"
                            value={item.saleValue}
                            onChange={(e) =>
                              updateSaleItem(idx, {
                                saleValue: Number(e.target.value),
                              })
                            }
                            sx={{ width: 110 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeSaleItem(idx)}
                            disabled={overrides.saleItens.length === 1}
                            title="Remover item"
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  <TextField
                    label="receivingDate (vazio = null → pendingReceivable)"
                    size="small"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={overrides.receivingDate}
                    onChange={(e) =>
                      updateOverride('receivingDate', e.target.value)
                    }
                    fullWidth
                  />

                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={overrides.responsibleEnabled}
                          onChange={(e) =>
                            updateOverride(
                              'responsibleEnabled',
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label="Adicionar responsável financeiro"
                    />
                    {overrides.responsibleEnabled && (
                      <Stack direction="row" spacing={1} mt={1}>
                        <TextField
                          label="idMember responsável"
                          size="small"
                          value={overrides.responsible.idMember}
                          onChange={(e) =>
                            updateOverride('responsible', {
                              ...overrides.responsible,
                              idMember: e.target.value,
                            })
                          }
                          sx={{ width: 180 }}
                        />
                        <TextField
                          label="idResponsible"
                          size="small"
                          value={overrides.responsible.idResponsible}
                          onChange={(e) =>
                            updateOverride('responsible', {
                              ...overrides.responsible,
                              idResponsible: e.target.value,
                            })
                          }
                          sx={{ width: 180 }}
                        />
                        <TextField
                          label="Nome do responsável"
                          size="small"
                          value={overrides.responsible.name}
                          onChange={(e) =>
                            updateOverride('responsible', {
                              ...overrides.responsible,
                              name: e.target.value,
                            })
                          }
                          fullWidth
                        />
                      </Stack>
                    )}
                  </Box>

                  {/* ---- Preview do payload ---- */}
                  <Accordion variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        Preview do payload final
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        component="pre"
                        sx={{
                          fontSize: 11,
                          background: '#f5f5f5',
                          p: 1,
                          borderRadius: 1,
                          maxHeight: 320,
                          overflow: 'auto',
                          m: 0,
                        }}
                      >
                        {JSON.stringify(finalPayload, null, 2)}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={firing}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleFire}
            disabled={!finalPayload || firing}
            startIcon={firing ? <CircularProgress size={16} /> : undefined}
          >
            {firing ? 'Disparando…' : 'Disparar cenário'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DevModePanel;
